// Pure rules for turning stored settings plus the device preference into a theme and an
// appearance. No DOM, no storage, no React. The inline script in index.html repeats these
// rules (it cannot import); tests/theme/prepaint.test.mjs runs both on one table of inputs.
import { DEFAULT_THEME_ID, APPEARANCE_SETTINGS, THEMES, getTheme } from './registry.js'

export const STORAGE_KEY = 'clairvos-engine-theme'
// Old key from before themes: the string `light` or `dark`. Read when the new key is absent, never deleted.
export const LEGACY_STORAGE_KEY = 'clairvos-admin-theme'

export function effectiveAppearance(theme, setting, prefersDark) {
  const wanted = setting === 'system' ? (prefersDark ? 'dark' : 'light') : setting
  return theme.appearances.includes(wanted) ? wanted : theme.defaultAppearance
}

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v)

// stored and legacy are the raw strings from storage, or null when absent or unreadable.
// Returns the settings to use plus `problems`, one plain sentence per stored value that was
// not recognized. A problem never throws: the caller logs it, shows the notice and persists the reset.
export function resolveInitial({ stored, legacy, prefersDark, themes = THEMES }) {
  const problems = []
  let themeId = DEFAULT_THEME_ID
  let appearanceSetting = 'system'
  let migrated = false

  if (stored != null) {
    let parsed
    try { parsed = JSON.parse(stored) } catch { parsed = undefined }
    if (!isPlainObject(parsed)) {
      problems.push('The saved theme setting could not be read.')
    } else {
      if (parsed.theme !== undefined) {
        const found = typeof parsed.theme === 'string' ? getTheme(parsed.theme, themes) : null
        if (found && found.status === 'available') themeId = found.id
        else if (found) problems.push(`The saved theme "${parsed.theme}" is not available yet.`)
        else problems.push(`The saved theme "${String(parsed.theme)}" is not a known theme.`)
      }
      if (parsed.appearance !== undefined) {
        if (APPEARANCE_SETTINGS.includes(parsed.appearance)) appearanceSetting = parsed.appearance
        else problems.push(`The saved appearance "${String(parsed.appearance)}" is not light, dark or system.`)
      }
    }
  } else if (legacy != null) {
    if (legacy === 'light' || legacy === 'dark') {
      appearanceSetting = legacy
      migrated = true
    } else {
      problems.push(`The old saved appearance "${legacy}" is not light or dark.`)
    }
  }

  const theme = getTheme(themeId, themes)
  return { themeId, appearanceSetting, appearance: effectiveAppearance(theme, appearanceSetting, prefersDark), migrated, problems }
}
